import React, { useEffect, useState } from 'react';
import ShopifyBuyButton from './ShopifyBuyButton';
import { getShopifySettings } from '../lib/shopifyClient';

/**
 * TrackBuyButton — payment-layer wrapper.
 *
 * Renders a Shopify Buy Button (product component) for a track when the
 * admin has mapped that track to a Shopify product id. If the track has no
 * mapping yet, renders a quiet "Coming soon" tag so we never show a broken
 * checkout path. Uses the shared singleton Shopify UI (lib/shopifyClient.js)
 * so the cart icon in the navbar reflects items added from any track card.
 */
const loadShopifySettings = getShopifySettings;

const inlineButtonOptions = {
  product: {
    contents: { img: false, title: false, price: false, options: false, description: false, button: true },
    styles: {
      product: { 'max-width': '100%', 'margin': '0' },
      button: {
        'background-color': '#D4AF37',
        color: '#000000',
        'font-family': 'Outfit, sans-serif',
        'font-weight': '600',
        'letter-spacing': '0.05em',
        'text-transform': 'uppercase',
        'padding': '10px 14px',
        ':hover': { 'background-color': '#E5C048' },
        ':focus': { 'background-color': '#E5C048' },
      },
    },
    text: { button: 'Buy' },
  },
  modalProduct: {
    contents: { img: false, imgWithCarousel: true, button: false, buttonWithQuantity: true },
    styles: {
      button: {
        'background-color': '#D4AF37',
        color: '#000000',
        ':hover': { 'background-color': '#E5C048' },
        ':focus': { 'background-color': '#E5C048' },
      },
    },
    text: { button: 'Add to cart' },
  },
  cart: {
    text: { total: 'Subtotal', button: 'Checkout' },
    styles: {
      button: {
        'background-color': '#D4AF37',
        color: '#000000',
        ':hover': { 'background-color': '#E5C048' },
        ':focus': { 'background-color': '#E5C048' },
      },
    },
  },
};

const TrackBuyButton = ({ track, compact = false }) => {
  const [shop, setShop] = useState(null);
  useEffect(() => {
    let alive = true;
    loadShopifySettings().then((s) => {
      if (alive) setShop(s);
    });
    return () => { alive = false; };
  }, []);

  if (!track || !track.shopify_product_id) {
    return (
      <span
        className={`text-[10px] uppercase tracking-wider text-[#737373] ${compact ? '' : 'inline-block'}`}
        data-testid={`track-buy-unavailable-${track && track.id}`}
        title="Not yet wired to Shopify"
      >
        Coming soon
      </span>
    );
  }

  // Settings still loading — render nothing (avoids a fallback flash and
  // avoids a false-positive missing-creds log on the first tick).
  if (shop === null) {
    return null;
  }

  if (!shop.domain || !shop.token) {
    // shopify_product_id is set but the global Shopify storefront credentials
    // are missing from settings.homepage. This breaks BUY across the whole
    // site. Surface a visible fallback so we never silently render nothing.
    if (typeof window !== 'undefined' && !window.__loggedShopifyCredsMissing) {
      window.__loggedShopifyCredsMissing = true;
      console.error(
        '[TrackBuyButton] Shopify storefront credentials are missing from settings.homepage ' +
        '(shopify_shop_domain / shopify_storefront_token). All BUY buttons will fall back to ' +
        '"Coming soon" until an admin sets them on the Albums tab → Shopify storefront credentials.'
      );
    }
    return (
      <span
        className={`text-[10px] uppercase tracking-wider text-[#737373] ${compact ? '' : 'inline-block'}`}
        data-testid={`track-buy-no-shop-creds-${track && track.id}`}
        title="Shopify storefront credentials missing — set them in Admin → Albums → Shopify storefront credentials"
      >
        Coming soon
      </span>
    );
  }

  return (
    <div
      className={compact ? 'inline-block' : 'w-full'}
      onClick={(e) => e.stopPropagation()}
      data-testid={`track-buy-${track.id}`}
    >
      <ShopifyBuyButton
        shopDomain={shop.domain}
        storefrontToken={shop.token}
        componentType="product"
        componentId={track.shopify_product_id}
        options={inlineButtonOptions}
      />
    </div>
  );
};

export default TrackBuyButton;
